import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  FiTruck,
  FiCreditCard,
  FiFileText,
  FiMessageSquare,
  FiHome,
  FiCheckCircle,
  FiPackage,
  FiCalendar,
  FiUser,
  FiMapPin,
  FiPhone,
} from "react-icons/fi";
import {
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  Home,
  Building,
  Download,
  HelpCircle,
  ArrowLeft,
  Package,
} from "lucide-react";
import AOS from "aos";
import "aos/dist/aos.css";
import { useCurrency } from "../../context/CurrencyContext";
import { formatingPrice } from "../../utils/formatingPrice";
import { useTranslation } from "react-i18next";

function OrderDetails() {
  const { orderId } = useParams();
  const { currency, convertPrice } = useCurrency();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Helper function to get localized text
  const getLocalizedText = (item, field) => {
    if (!item || !item[field]) return "";
    
    // If value is a string (backward compatibility)
    if (typeof item[field] === 'string') {
      return item[field];
    }
    
    // If value is an object with language keys
    if (typeof item[field] === 'object') {
      return item[field][currentLanguage] || 
             item[field].en || 
             item[field].hi || 
             item[field].es || 
             item[field].zh || 
             "";
    }
    
    return "";
  };

  useEffect(() => {
    AOS.init({ duration: 800, easing: "ease-out", once: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const fetchOrderDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/orders/get-order-by-id/${orderId}`
      );
      
      const orderData = response?.data;
      
      // Add localized names to products
      if (orderData?.products) {
        orderData.products = orderData.products.map(item => ({
          ...item,
          product: {
            ...item.product,
            localizedName: getLocalizedText(item.product, "name"),
            localizedDescription: getLocalizedText(item.product, "description"),
            localizedMaterial: getLocalizedText(item.product, "material"),
            localizedMeasurement: getLocalizedText(item.product, "measurement"),
          }
        }));
      }
      
      setOrder(orderData);
    } catch (err) {
      console.error(err);
      setError(t("order_details.fetch_failed") || "Failed to fetch order details.");
    } finally {
      setLoading(false);
    }
  };

  // Update localized names when language changes
  useEffect(() => {
    if (order?.products) {
      const updatedOrder = {
        ...order,
        products: order.products.map(item => ({
          ...item,
          product: {
            ...item.product,
            localizedName: getLocalizedText(item.product, "name"),
            localizedDescription: getLocalizedText(item.product, "description"),
            localizedMaterial: getLocalizedText(item.product, "material"),
            localizedMeasurement: getLocalizedText(item.product, "measurement"),
          }
        }))
      };
      setOrder(updatedOrder);
    }
  }, [currentLanguage]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    if (orderId) fetchOrderDetails();
  }, [orderId]);

  const calculatedTotals = useMemo(() => {
    if (!order || !order.products)
      return { subtotal: 0, shipping: 0, gst: 0, extraTax: 0, total: 0 };

    const subtotal = order.products.reduce(
      (sum, item) => sum + (item.product?.price || 0) * (item.quantity || 1),
      0
    );

    const shipping = order.products.reduce(
      (sum, item) =>
        sum + (item.product?.shippingCharge || 0) * (item.quantity || 1),
      0
    );

    const gst = order.products.reduce(
      (sum, item) =>
        sum +
        ((item.product?.price || 0) *
          (item.quantity || 1) *
          (item.product?.gstPercentage || 0)) /
          100,
      0
    );

    const extraTax = order.products.reduce(
      (sum, item) =>
        sum +
        ((item.product?.price || 0) *
          (item.quantity || 1) *
          (item.product?.extraTaxPercentage || 0)) /
          100,
      0
    );

    const total = subtotal + shipping + gst + extraTax;
    return { subtotal, shipping, gst, extraTax, total };
  }, [order]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "text-yellow-600 bg-yellow-100 border-yellow-200";
      case "shipped":
        return "text-purple-600 bg-purple-100 border-purple-200";
      case "delivered":
        return "text-green-600 bg-green-100 border-green-200";
      case "cancelled":
        return "text-red-600 bg-red-100 border-red-200";
      case "processing":
        return "text-blue-600 bg-blue-100 border-blue-200";
      default:
        return "text-gray-600 bg-gray-100 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return <Clock className="w-4 h-4" />;
      case "shipped":
        return <Truck className="w-4 h-4" />;
      case "delivered":
        return <CheckCircle className="w-4 h-4" />;
      case "cancelled":
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusDisplayText = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return t("order_details.status_pending") || "Pending";
      case "shipped":
        return t("order_details.status_shipped") || "Shipped";
      case "delivered":
        return t("order_details.status_delivered") || "Delivered";
      case "cancelled":
        return t("order_details.status_cancelled") || "Cancelled";
      case "processing":
        return t("order_details.status_processing") || "Processing";
      default:
        return status || t("order_details.status_unknown") || "Unknown";
    }
  };

  const getAddressTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "home":
        return <Home className="w-4 h-4" />;
      case "work":
      case "office":
        return <Building className="w-4 h-4" />;
      default:
        return <Home className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFBFB]">
        <div className="p-6">
          <div className="animate-pulse space-y-6">
            {/* Breadcrumb Skeleton */}
            <div className="h-6 bg-gray-300 rounded w-3/4 mb-6"></div>
            
            {/* Main Content Skeleton */}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left Section Skeleton */}
              <div className="w-full lg:w-2/3 space-y-6">
                <div className="bg-white rounded-lg p-6">
                  <div className="h-6 bg-gray-300 rounded w-1/3 mb-4"></div>
                  {[1, 2].map((i) => (
                    <div key={i} className="border rounded-lg p-4 mb-4 flex gap-4">
                      <div className="w-24 h-24 bg-gray-300 rounded"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                        <div className="h-3 bg-gray-300 rounded w-full"></div>
                        <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Right Section Skeleton */}
              <div className="w-full lg:w-1/3">
                <div className="bg-white rounded-lg p-6">
                  <div className="h-6 bg-gray-300 rounded w-1/3 mb-4"></div>
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="flex justify-between">
                        <div className="h-4 bg-gray-300 rounded w-1/3"></div>
                        <div className="h-4 bg-gray-300 rounded w-1/4"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FBFBFB] flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t("order_details.error") || "Error"}
            </h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate("/user-orders")}
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg transition-colors"
            >
              {t("order_details.back_to_orders") || "Back to Orders"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#FBFBFB] flex items-center justify-center">
        <div className="text-center">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md">
            <Package className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t("order_details.order_not_found") || "Order Not Found"}
            </h2>
            <p className="text-gray-600 mb-4">
              {t("order_details.order_not_found_message") || "The order you're looking for doesn't exist."}
            </p>
            <button
              onClick={() => navigate("/user-orders")}
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg transition-colors"
            >
              {t("order_details.back_to_orders") || "Back to Orders"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBFBFB]">
      {/* Breadcrumb */}
      <div className="bg-gray-800 text-white">
        <div className="py-4 px-4 md:px-10">
          <nav className="flex items-center space-x-2 text-sm">
            <button
              onClick={() => navigate("/")}
              className="flex items-center hover:text-orange-400 transition-colors"
            >
              <Home className="w-4 h-4" />
            </button>
            <span className="text-gray-400">/</span>
            <button
              onClick={() => navigate("/user-orders")}
              className="hover:text-orange-400 transition-colors"
            >
              {t("order_details.my_orders") || "My Orders"}
            </button>
            <span className="text-gray-400">/</span>
            <span className="text-orange-400 truncate max-w-xs md:max-w-md">
              {t("order_details.order") || "Order"} #{order._id?.slice(-8)}
            </span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 md:px-10 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate("/user-orders")}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("order_details.back_to_orders") || "Back to Orders"}
          </button>
          
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-philosopher text-gray-900">
                {t("order_details.order") || "Order"} #{order._id?.slice(-8)}
              </h1>
              <p className="text-gray-600 mt-1 flex items-center">
                <FiCalendar className="w-4 h-4 mr-1" />
                {formatDate(order.createdAt)}
              </p>
            </div>
            
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(order.status)}`}>
              {getStatusIcon(order.status)}
              <span className="ml-1">{getStatusDisplayText(order.status)}</span>
            </span>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* LEFT SECTION */}
          <div className="w-full lg:w-2/3 space-y-6">
            {/* Order Products */}
            <div className="bg-white rounded-xl shadow-sm border p-6" data-aos="fade-up">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  <FiPackage className="inline mr-2 text-orange-500" />
                  {t("order_details.order_items") || "Order Items"}
                </h2>
                <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                  {order.products?.length || 0} {order.products?.length === 1 
                    ? t("order_details.item_singular") || "item" 
                    : t("order_details.item_plural") || "items"}
                </span>
              </div>

              <div className="space-y-4">
                {order.products?.map((item, idx) => {
                  const productName = item.product?.localizedName || 
                                     getLocalizedText(item.product, "name") || 
                                     t("order_details.untitled_product") || "Untitled Product";
                  const productDescription = item.product?.localizedDescription || 
                                           getLocalizedText(item.product, "description") || 
                                           t("order_details.no_description") || "No description available.";
                  const productImage = item.product?.images?.[0] || "https://via.placeholder.com/150x150?text=Product";

                  return (
                    <div
                      key={idx}
                      className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors"
                      data-aos="fade-up"
                      data-aos-delay={idx * 100}
                    >
                      <div className="flex flex-col sm:flex-row gap-4">
                        {/* Product Image */}
                        <div className="w-full sm:w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                          <img
                            src={productImage}
                            alt={productName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/150x150?text=Product";
                            }}
                          />
                        </div>

                        {/* Product Details */}
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                            <div>
                              <h3 className="font-semibold text-gray-900 line-clamp-2">
                                {productName}
                              </h3>
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                {productDescription}
                              </p>
                              
                              {/* Product Specifications */}
                              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                                {item.product?.localizedMaterial && (
                                  <div className="flex items-center">
                                    <span className="text-gray-500">
                                      {t("order_details.material") || "Material"}: 
                                    </span>
                                    <span className="ml-1 font-medium">
                                      {item.product.localizedMaterial}
                                    </span>
                                  </div>
                                )}
                                
                                {item.product?.localizedMeasurement && (
                                  <div className="flex items-center">
                                    <span className="text-gray-500">
                                      {t("order_details.measurement") || "Measurement"}: 
                                    </span>
                                    <span className="ml-1 font-medium">
                                      {item.product.localizedMeasurement}
                                    </span>
                                  </div>
                                )}
                                
                                {item.product?.brand && (
                                  <div className="flex items-center">
                                    <span className="text-gray-500">
                                      {t("order_details.brand") || "Brand"}: 
                                    </span>
                                    <span className="ml-1 font-medium">
                                      {item.product.brand}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Price and Quantity */}
                            <div className="text-right">
                              <div className="text-lg font-bold text-gray-900">
                                {formatingPrice(
                                  convertPrice(item.product?.price || 0),
                                  currency
                                )}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                {t("order_details.quantity") || "Qty"}: {item.quantity || 1}
                              </div>
                              <div className="text-sm font-semibold text-gray-900 mt-2">
                                {t("order_details.subtotal") || "Subtotal"}:{" "}
                                {formatingPrice(
                                  convertPrice((item.product?.price || 0) * (item.quantity || 1)),
                                  currency
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Item Breakdown */}
                          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <div className="text-gray-500">
                                {t("order_details.price") || "Price"}
                              </div>
                              <div className="font-medium">
                                {formatingPrice(convertPrice(item.price || 0), currency)}
                              </div>
                            </div>
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <div className="text-gray-500">
                                {t("order_details.gst") || "GST"}
                              </div>
                              <div className="font-medium">
                                {formatingPrice(convertPrice(item.gst || 0), currency)}
                              </div>
                            </div>
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <div className="text-gray-500">
                                {t("order_details.shipping") || "Shipping"}
                              </div>
                              <div className="font-medium">
                                {formatingPrice(convertPrice(item.shippingCharge || 0), currency)}
                              </div>
                            </div>
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <div className="text-gray-500">
                                {t("order_details.item_total") || "Item Total"}
                              </div>
                              <div className="font-medium">
                                {formatingPrice(
                                  convertPrice((item.price || 0) + (item.gst || 0) + (item.shippingCharge || 0)),
                                  currency
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-white rounded-xl shadow-sm border p-6" data-aos="fade-up">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <FiMapPin className="text-orange-500 mr-2" />
                {t("order_details.shipping_address") || "Shipping Address"}
              </h3>

              <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    {getAddressTypeIcon(order.shippingAddress?.addressType)}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-900 text-lg">
                          {order.shippingAddress?.fullName}
                        </h4>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {order.shippingAddress?.addressType || "Home"}
                        </span>
                      </div>
                      
                      {order.shippingAddress?.isDefault && (
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full font-medium">
                          {t("order_details.default") || "DEFAULT"}
                        </span>
                      )}
                    </div>

                    <div className="space-y-2 text-gray-700">
                      <p className="font-medium">{order.shippingAddress?.addressLine1}</p>
                      {order.shippingAddress?.addressLine2 && (
                        <p>{order.shippingAddress?.addressLine2}</p>
                      )}
                      <p>
                        {order.shippingAddress?.city}, {order.shippingAddress?.state} - {order.shippingAddress?.postalCode}
                      </p>
                      <p>{order.shippingAddress?.country}</p>
                      
                      <div className="flex items-center gap-2 text-gray-700 mt-3">
                        <FiPhone className="w-4 h-4" />
                        <span className="font-medium">{order.shippingAddress?.phone}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SECTION - Order Summary */}
          <div className="w-full lg:w-1/3">
            <div className="bg-white rounded-xl shadow-sm border p-6 sticky lg:top-6" data-aos="fade-left">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <FiCreditCard className="text-orange-500 mr-2" />
                {t("order_details.order_summary") || "Order Summary"}
              </h2>

              <div className="space-y-3">
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">{t("order_details.subtotal") || "Subtotal"}</span>
                  <span className="font-medium">
                    {formatingPrice(convertPrice(calculatedTotals.subtotal), currency)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">{t("order_details.shipping") || "Shipping"}</span>
                  <span className="font-medium">
                    {formatingPrice(convertPrice(calculatedTotals.shipping), currency)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">{t("order_details.gst") || "GST"}</span>
                  <span className="font-medium">
                    {formatingPrice(convertPrice(calculatedTotals.gst), currency)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">{t("order_details.extra_tax") || "Extra Tax"}</span>
                  <span className="font-medium">
                    {formatingPrice(convertPrice(calculatedTotals.extraTax), currency)}
                  </span>
                </div>

                <div className="border-t pt-4 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">{t("order_details.total") || "Total"}</span>
                    <span className="text-xl font-bold text-orange-600">
                      {formatingPrice(convertPrice(calculatedTotals.total), currency)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t("order_details.including_all_taxes") || "Including all taxes and shipping charges"}
                  </p>
                </div>
              </div>

              {/* Order Timeline */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <FiCheckCircle className="text-green-500 mr-2" />
                  {t("order_details.order_timeline") || "Order Timeline"}
                </h3>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{t("order_details.order_placed") || "Order Placed"}</p>
                      <p className="text-xs text-gray-500">{formatDate(order.createdAt)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      order.status === "Delivered" ? "bg-green-100" : "bg-gray-100"
                    }`}>
                      {order.status === "Delivered" ? (
                        <CheckCircle className="w-3 h-3 text-green-600" />
                      ) : (
                        <Clock className="w-3 h-3 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{t("order_details.delivery") || "Delivery"}</p>
                      <p className="text-xs text-gray-500">
                        {order.status === "Delivered" 
                          ? (order.updatedAt ? formatDate(order.updatedAt) : t("order_details.completed") || "Completed")
                          : t("order_details.in_progress") || "In Progress"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 space-y-3">
                <button className="w-full flex items-center justify-center py-3 px-4 border border-orange-600 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors">
                  <HelpCircle className="w-4 h-4 mr-2" />
                  {t("order_details.need_help") || "Need Help?"}
                </button>
                <button className="w-full flex items-center justify-center py-3 px-4 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors">
                  <Download className="w-4 h-4 mr-2" />
                  {t("order_details.download_invoice") || "Download Invoice"}
                </button>
                
                {/* Exchange Request Status */}
                {order.exchangeRequest?.isRequested && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FiCheckCircle className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">
                        {t("order_details.exchange_requested") || "Exchange Requested"}
                      </span>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      {t("order_details.exchange_status") || "Status"}: {order.exchangeRequest?.status}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderDetails;






// import React, { useEffect, useState, useMemo } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import axios from "axios";
// import {
//   FiTruck,
//   FiCreditCard,
//   FiFileText,
//   FiMessageSquare,
//   FiHome,
//   FiCheckCircle,
// } from "react-icons/fi";
// import AOS from "aos";
// import "aos/dist/aos.css";
// import { useCurrency } from "../../context/CurrencyContext";
// import { formatingPrice } from "../../utils/formatingPrice";

// function OrderDetails() {
//   const { orderId } = useParams();
//   const { currency, convertPrice } = useCurrency();

//   const [order, setOrder] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const navigate = useNavigate();

//   useEffect(() => {
//     AOS.init({ duration: 800, easing: "ease-out", once: true });
//     window.scrollTo({ top: 0, behavior: "smooth" });
//   }, []);
//   const dataById = async () => {
//     setLoading(true);
//     try {
//       const response = await axios.get(
//         `${process.env.REACT_APP_API_URL}/orders/get-order-by-id/${orderId}`
//       );
//       setOrder(response?.data);
//     } catch (err) {
//       console.error(err);
//       setError("Failed to fetch order details.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const formatPrice = (price) =>
//     new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//       maximumFractionDigits: 0,
//     }).format(price || 0);

//   useEffect(() => {
//     if (orderId) dataById();
//   }, [orderId]);

//   const calculatedTotals = useMemo(() => {
//     if (!order || !order.products)
//       return { subtotal: 0, shipping: 0, gst: 0, extraTax: 0, total: 0 };

//     const subtotal = order.products.reduce(
//       (sum, item) => sum + (item.product?.price || 0) * (item.quantity || 1),
//       0
//     );

//     const shipping = order.products.reduce(
//       (sum, item) =>
//         sum + (item.product?.shippingCharge || 0) * (item.quantity || 1),
//       0
//     );

//     const gst = order.products.reduce(
//       (sum, item) =>
//         sum +
//         ((item.product?.price || 0) *
//           (item.quantity || 1) *
//           (item.product?.gstPercentage || 0)) /
//           100,
//       0
//     );

//     const extraTax = order.products.reduce(
//       (sum, item) =>
//         sum +
//         ((item.product?.price || 0) *
//           (item.quantity || 1) *
//           (item.product?.extraTaxPercentage || 0)) /
//           100,
//       0
//     );

//     const total = subtotal + shipping + gst + extraTax;
//     return { subtotal, shipping, gst, extraTax, total };
//   }, [order]);
//   if (loading) {
//     return (
//       <div className="p-6 animate-pulse">
//         <div className="w-full h-5 bg-gray-300 mb-4 rounded"></div>
//         <div className="flex flex-col gap-4">
//           <div className="h-32 bg-gray-200 rounded"></div>
//           <div className="h-32 bg-gray-200 rounded"></div>
//           <div className="h-20 bg-gray-200 rounded"></div>
//         </div>
//       </div>
//     );
//   }

//   if (error) return <div className="p-8 text-center">{error}</div>;
//   if (!order) return <div className="p-8 text-center">Order Not Found</div>;

//   return (
//     <>
//       {/* Breadcrumb */}
//       <div className="text-sm flex bg-slate-700 text-white">
//         <div className="py-4 px-4 md:px-10 flex flex-wrap gap-2">
//           <span
//             onClick={() => navigate("/")}
//             className="cursor-pointer hover:underline"
//           >
//             Home
//           </span>{" "}
//           /
//           <span
//             onClick={() => navigate("/user-profile")}
//             className="cursor-pointer hover:underline"
//           >
//             My Account
//           </span>{" "}
//           /
//           <span
//             onClick={() => navigate("/user-orders")}
//             className="cursor-pointer hover:underline"
//           >
//             My Orders
//           </span>{" "}
//           /<span className="text-gray-300">Order #{order._id?.slice(-8)}</span>
//         </div>
//       </div>

//       {/* Main Content */}
//       <div className="px-4 md:px-10 py-8 w-full mx-auto">
//         <div className="flex flex-col lg:flex-row gap-6">
//           {/* LEFT SECTION */}
//           <div className="w-full lg:w-2/3 space-y-6">
//             {/* Products Section */}
//             <div className="bg-white rounded-lg shadow-sm border p-6">
//               <h2 className="text-xl font-semibold mb-4 flex items-center">
//                 <FiCheckCircle className="text-green-500 mr-2" /> Order Details
//               </h2>

//               {order.products.map((item, idx) => (
//                 <div
//                   key={idx}
//                   className="border rounded-lg p-4 mb-4 flex gap-4 flex-col sm:flex-row"
//                 >
//                   {/* Responsive Image */}
//                   <div className="w-full sm:w-24 h-40 sm:h-24 bg-gray-100 rounded flex items-center justify-center">
//                     {item.product?.images?.[0] ? (
//                       <img
//                         src={item.product.images[0]}
//                         alt={item.product?.name}
//                         className="w-full h-full object-contain"
//                       />
//                     ) : (
//                       <span className="text-gray-400 text-xs">No Image</span>
//                     )}
//                   </div>

//                   {/* Product Info */}
//                   <div className="flex-1 text-sm">
//                     <h3 className="font-medium">{item.product?.name}</h3>
//                     <p className="text-gray-600">
//                       {item.product?.description || "No description."}
//                     </p>
//                     <p className="mt-2 font-semibold">
//                       Total:{" "}
//                       {formatingPrice(
//                         convertPrice(
//                           formatPrice(
//                             (item.product?.price || 0) * item.quantity
//                           )
//                         ),
//                         currency
//                       )}
//                       {/* {formatPrice((item.product?.price || 0) * item.quantity)} */}
//                     </p>
//                   </div>
//                 </div>
//               ))}
//             </div>

//             {/* Delivery Timeline */}
//             <div className="bg-white rounded-lg shadow-sm border p-6">
//               <h3 className="font-medium mb-4 flex items-center">
//                 <FiTruck className="text-blue-600 mr-2" /> Delivery Timeline
//               </h3>

//               <div className="space-y-4 text-sm">
//                 <div className="flex items-start gap-3">
//                   <FiCheckCircle className="text-green-500 mt-1" />
//                   <div>
//                     <p className="font-medium">Order Confirmed</p>
//                     <p className="text-gray-500">
//                       {new Date(order.createdAt).toLocaleString("en-IN")}
//                     </p>
//                   </div>
//                 </div>

//                 <div className="flex items-start gap-3">
//                   <FiCheckCircle
//                     className={`mt-1 ${
//                       order.status === "Delivered"
//                         ? "text-green-500"
//                         : "text-gray-400"
//                     }`}
//                   />
//                   <div>
//                     <p className="font-medium">Delivered</p>
//                     <p className="text-gray-500">
//                       {order.deliveredAt
//                         ? new Date(order.deliveredAt).toLocaleString("en-IN")
//                         : "In transit"}
//                     </p>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             <div className="bg-white rounded-lg shadow-sm border p-6">
//               <h3 className="font-medium mb-4 flex items-center">
//                 <FiHome className="text-blue-600 mr-2" /> Delivery Address
//               </h3>
//               <div className="text-sm text-gray-700 space-y-1">
//                 <p className="font-medium">{order.shippingAddress?.fullName}</p>
//                 <p>
//                   {order.shippingAddress?.addressLine1},{" "}
//                   {order.shippingAddress?.addressLine2}
//                 </p>
//                 <p>
//                   {order.shippingAddress?.city}, {order.shippingAddress?.state}{" "}
//                   - {order.shippingAddress?.postalCode}
//                 </p>
//                 <p>{order.shippingAddress?.country}</p>
//                 <p>Phone: {order.shippingAddress?.phone}</p>
//               </div>
//             </div>
//           </div>

//           {/* RIGHT SECTION (Price Summary) */}
//           <div className="w-full lg:w-1/3">
//             <div className="bg-white rounded-lg shadow-sm border p-6 sticky lg:top-4">
//               <h2 className="text-xl font-semibold mb-4 flex items-center">
//                 <FiCreditCard className="text-blue-600 mr-2" /> Price Details
//               </h2>

//               <div className="text-sm space-y-2">
//                 <div className="flex justify-between">
//                   <span>Amount:</span>
//                   <span>
//                     {formatingPrice(
//                       convertPrice(formatPrice(calculatedTotals.subtotal)),
//                       currency
//                     )}
//                   </span>
//                   {/* <span>{formatPrice(calculatedTotals.subtotal)}</span> */}
//                 </div>
//                 <div className="flex justify-between">
//                   <span>Shipping:</span>
//                   <span>
//                     {formatingPrice(
//                       convertPrice(formatPrice(calculatedTotals.shipping)),
//                       currency
//                     )}
//                   </span>
//                   {/* <span>{formatPrice(calculatedTotals.shipping)}</span> */}
//                 </div>
//                 <div className="flex justify-between">
//                   <span>GST:</span>
//                   <span>
//                     {formatingPrice(
//                       convertPrice(formatPrice(calculatedTotals.gst)),
//                       currency
//                     )}
//                   </span>
//                   {/* <span>{formatPrice(calculatedTotals.gst)}</span> */}
//                 </div>
//                 <div className="flex justify-between">
//                   <span>Extra Tax:</span>
//                   <span>
//                     {formatingPrice(
//                       convertPrice(formatPrice(calculatedTotals.extraTax)),
//                       currency
//                     )}
//                   </span>
//                   {/* <span>{formatPrice(calculatedTotals.extraTax)}</span> */}
//                 </div>

//                 <div className="border-t pt-2 flex justify-between font-semibold">
//                   <span>Total:</span>
//                   <span>
//                     {formatingPrice(
//                       convertPrice(formatPrice(calculatedTotals.total)),
//                       currency
//                     )}
//                   </span>
//                   {/* <span>{formatPrice(calculatedTotals.total)}</span> */}
//                 </div>
//               </div>

//               <div className="mt-4 flex flex-col gap-2">
//                 <button className="w-full flex items-center justify-center py-2 px-4 border border-blue-600 text-blue-600 rounded">
//                   <FiMessageSquare className="mr-2" /> Need Help?
//                 </button>
//                 <button className="w-full flex items-center justify-center py-2 px-4 border border-blue-600 text-blue-600 rounded">
//                   <FiFileText className="mr-2" /> Download Invoice
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// }

// export default OrderDetails;
